import { Grid, Paper, Typography } from '@mui/material'
import { Box } from '@mui/system'
import React, { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { logger } from 'common/lib/logger'
import { values } from 'lodash'
import { determineMimeType } from '../operations/mutations/uploadMutations'

interface ValueStr {
    data: string
    filename?: string
}

interface ValueJson {
    data: any
    filename?: string
    name?: string
}

interface ValueBinary {
    data: ArrayBuffer
    filename?: string
    name?: string
}

interface FileUploadBase {
    error?: boolean
    helperText?: string
    name?: string
    allowedExtensions?: string[]
    description: string
}

interface FileUploadJSON extends FileUploadBase {
    onChange?: (e: any) => void
    contentType: 'application/json'
    onValueChange?: (value: ValueJson) => void
    value?: ValueJson
}

interface FileUploadMP4 extends FileUploadBase {
    onChange?: (e: any) => void
    contentType: 'video/mp4'
    onValueChange?: (value: ValueBinary) => void
    value?: ValueBinary
}

interface FileUploadGeneral extends FileUploadBase {
    onChange?: (e: any) => void
    onValueChange?: (value: ValueStr) => void
    value?: ValueStr
    contentType?: undefined
}

type FileUploadProps = FileUploadGeneral | FileUploadJSON | FileUploadMP4

export const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
    ({ error, helperText, onChange, name, allowedExtensions, onValueChange, contentType, description, value }, ref) => {
        const [errorMessage, setErrorMessage] = useState<null | string>(null)
        const [filenameState, setFilename] = useState<string>()

        useEffect(() => {
            if (!value?.filename && typeof value?.data == 'string' && value.data.startsWith('http')) {
                fetch(value.data, {
                    method: 'HEAD',
                }).then((res) => {
                    const filenameMeta = res.headers.get('x-amz-meta-filename')
                    if (filenameMeta) setFilename(filenameMeta)
                })
            }
        }, [value])

        const onChangeHandler = useCallback(
            (file: File) => {
                const extension = file.name.split('.').pop()
                const reader = new FileReader()

                reader.onloadend = function() {
                    if (reader.result) {
                        const binaryData = reader.result as ArrayBuffer

                        if (file.type === 'application/json' || determineMimeType(extension) === 'application/json') {
                            try {
                                const value: ValueJson = {
                                    data: JSON.parse(reader.result as string),
                                    name,
                                    filename: file.name,
                                }

                                if (onChange) onChange({ target: { value, name } })
                                if (onValueChange && isValueJson(value)) {
                                    (onValueChange as (value: ValueJson) => void)(value)
                                }
                            } catch (error) {
                                console.error('Error parsing JSON:', error)
                            }
                        } else if (file.type === 'video/mp4' || determineMimeType(extension) === 'video/mp4') {
                            const value: ValueBinary = { data: binaryData, name, filename: file.name }

                            if (onChange) onChange({ target: { value, name } })
                            if (onValueChange && isValueBinary(value)) {
                                (onValueChange as (value: ValueBinary) => void)(value)
                            }
                        } else {
                            const url = URL.createObjectURL(file)
                            const value = { data: url, name, filename: file.name }

                            if (onChange) onChange({ target: { value, name } })
                            if (onValueChange) {
                                (onValueChange as (value: ValueStr) => void)(value)
                            }
                        }

                        return true
                    } else {
                        console.error('FileReader result is null')
                    }
                }

                if (file.type === 'video/mp4' || determineMimeType(extension) === 'video/mp4') {
                    reader.readAsArrayBuffer(file)
                } else {
                    reader.readAsText(file)
                }
            },
            [onChange, onValueChange, name],
        )

        function isValueBinary(value: any): value is ValueBinary {
            return value.data instanceof ArrayBuffer
        }

        function isValueJson(value: any): value is ValueJson {
            return typeof value.data === 'object' && value.data !== null
        }

        const onDrop = useCallback(
            async (acceptedFiles, refect, event) => {
                setErrorMessage('')
                const file = acceptedFiles[0]

                if (allowedExtensions && !allowedExtensions.some((ext) => file.name.endsWith(ext))) {
                    setErrorMessage('Podporovány jsou pouze soubory s příponou ' + allowedExtensions.join())
                    return
                }

                await onChangeHandler(file)
            },
            [onChangeHandler, setErrorMessage, allowedExtensions]
        )

        const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({ onDrop })
        const filename = value?.filename ? value.filename : filenameState ? filenameState : 'Importovat'

        return (
            <>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item>
                        <Paper
                            component="div"
                            {...getRootProps()}
                            elevation={0}
                            sx={{
                                width: 373,
                                height: 121,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                cursor: 'pointer',
                                backgroundColor: '#F7F7F7',
                            }}
                        >
                            <input ref={ref} name={name} {...getInputProps()} />
                            <Box>
                                {isDragActive ? (
                                    <p>Pusťe soubor zde...</p>
                                ) : (
                                    <>
                                        <Typography>{description}</Typography>
                                        <Typography textAlign="center">
                                            nebo <b>Procházet soubory</b>
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item>
                        <Typography sx={{ fontWeight: 'bold' }} color="secondary">
                            {filename}
                        </Typography>
                    </Grid>
                </Grid>
                {errorMessage ? <Typography color="error"> {errorMessage}</Typography> : null}
                {helperText ? <Typography color={error ? 'error' : 'inherit'}> {helperText}</Typography> : null}
            </>
        )
    }
)
